import { createAction, createFeatureSelector, createReducer, on, props } from '@ngrx/store';
import { defaultMessageQuery, MemberMessageDetail, MemberMessagePage, MemberMessageQuery } from '@app/core/services/MemberMessageService';

export const enter = createAction('[Member messages] Enter', props<{ adminUserId: string | null; readOnly: boolean }>());
export const leave = createAction('[Member messages] Leave');
export const loadList = createAction('[Member messages] Load list', props<{ query: MemberMessageQuery }>());
export const listLoaded = createAction('[Member messages] List loaded', props<{ page: MemberMessagePage }>());
export const loadDetail = createAction('[Member messages] Load detail', props<{ id: string }>());
export const detailLoaded = createAction('[Member messages] Detail loaded', props<{ message: MemberMessageDetail }>());
export const detailDisplayed = createAction('[Member messages] Detail displayed', props<{ id: string; version: string }>());
export const readConfirmed = createAction('[Member messages] Read confirmed', props<{ id: string; version: string; firstReadAtUtc: string | null }>());
export const loadPreferences = createAction('[Member messages] Load preferences');
export const savePreferences = createAction('[Member messages] Save preferences', props<{ enabled: boolean }>());
export const preferencesLoaded = createAction('[Member messages] Preferences loaded', props<{ enabled: boolean }>());
export const failed = createAction('[Member messages] Failed', props<{ operation: 'list' | 'detail' | 'read' | 'preferences'; message: string }>());

export interface MemberMessagesState {
  adminUserId: string | null;
  readOnly: boolean;
  query: MemberMessageQuery;
  page: MemberMessagePage;
  listLoading: boolean;
  listError: string;
  detailId: string | null;
  detail: MemberMessageDetail | null;
  detailLoading: boolean;
  detailError: string;
  readError: string;
  pushEnabled: boolean | null;
  preferencesBusy: boolean;
  preferencesError: string;
}
export const initialState: MemberMessagesState = {
  adminUserId: null, readOnly: true, query: { ...defaultMessageQuery },
  page: { Items: [], TotalCount: 0, UnreadCount: 0 }, listLoading: true, listError: '',
  detailId: null, detail: null, detailLoading: false, detailError: '', readError: '',
  pushEnabled: null, preferencesBusy: false, preferencesError: ''
};
export const memberMessagesReducer = createReducer(initialState,
  on(enter, (_, context) => ({ ...initialState, adminUserId: context.adminUserId, readOnly: context.readOnly })),
  on(leave, () => ({ ...initialState })),
  on(loadList, (state, { query }) => ({ ...state, query, listLoading: true, listError: '', detail: null, detailId: null, detailError: '' })),
  on(listLoaded, (state, { page }) => ({ ...state, page, listLoading: false })),
  on(loadDetail, (state, { id }) => ({ ...state, detailId: id, detail: null, detailLoading: true, detailError: '', readError: '' })),
  on(detailLoaded, (state, { message }) => message.Id !== state.detailId ? state : ({ ...state, detail: message, detailLoading: false })),
  on(readConfirmed, (state, action) => {
    if (state.readOnly || state.detail?.Id !== action.id || state.detail.Version !== action.version || !action.firstReadAtUtc) return state;
    return { ...state, readError: '', detail: { ...state.detail, IsRead: true, FirstReadAtUtc: state.detail.FirstReadAtUtc || action.firstReadAtUtc } };
  }),
  on(loadPreferences, savePreferences, state => ({ ...state, preferencesBusy: true, preferencesError: '' })),
  on(preferencesLoaded, (state, { enabled }) => ({ ...state, pushEnabled: enabled, preferencesBusy: false, preferencesError: '' })),
  on(failed, (state, { operation, message }) => ({ ...state,
    ...(operation === 'list' ? { listLoading: false, listError: message } : {}),
    ...(operation === 'detail' ? { detailLoading: false, detailError: message } : {}),
    ...(operation === 'read' ? { readError: message } : {}),
    ...(operation === 'preferences' ? { preferencesBusy: false, preferencesError: message } : {})
  }))
);
export const selectMessages = createFeatureSelector<MemberMessagesState>('memberMessages');
